/**
 * Sentinel Platform - API Client
 * Axios instance with Auth0 token injection and multi-tenant awareness.
 */
import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const api = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Token getter - set by AuthProvider after login
let getAccessToken = null;

export function setTokenGetter(getter) {
  getAccessToken = getter;
}

// Request interceptor: attach Bearer token
api.interceptors.request.use(async (config) => {
  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('Failed to get access token:', err);
    }
  }
  return config;
});

// Response interceptor: handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================================
// TENANT ENDPOINTS
// ============================================================
export const tenantApi = {
  getBranding: (slug) => api.get(`/tenants/branding/${slug}`),
  list: () => api.get('/tenants'),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
};

// ============================================================
// TICKET ENDPOINTS
// ============================================================
export const ticketApi = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.patch(`/tickets/${id}`, data),
  addComment: (id, data) => api.post(`/tickets/${id}/comments`, data),
  getActivity: (id) => api.get(`/tickets/${id}/activity`),
  getDashboard: (tenantId) => api.get('/tickets/dashboard/summary', { params: tenantId ? { tenant_id: tenantId } : {} }),
  getDashboardCharts: (tenantId) => api.get('/tickets/dashboard/charts', { params: tenantId ? { tenant_id: tenantId } : {} }),
  getComments: (id) => api.get(`/tickets/${id}/comments`),
};

// ============================================================
// ASSET ENDPOINTS
// ============================================================
export const assetApi = {
  list: (params) => api.get('/assets', { params }),
  get: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data),
  update: (id, data) => api.patch(`/assets/${id}`, data),
};

// ============================================================
// MONITORING ENDPOINTS
// ============================================================
export const monitoringApi = {
  getOverview: (tenantId) => api.get('/monitoring/overview', { params: tenantId ? { tenant_id: tenantId } : {} }),
  getMonitoredAssets: (tenantId) => api.get('/monitoring/assets', { params: tenantId ? { tenant_id: tenantId } : {} }),
  getAssetTelemetry: (assetId, limit) => api.get(`/monitoring/assets/${assetId}/telemetry`, { params: { limit: limit || 60 } }),
  getAlerts: (params) => api.get('/monitoring/alerts', { params }),
  queueCommand: (data) => api.post('/monitoring/commands', data),
  getCommands: (params) => api.get('/monitoring/commands', { params }),
};

export default api;
